'use client';

import React from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import store from 'src/redux/store';
import { useDebounce } from 'use-debounce';
import { useGetCodeSuggestionsQuery } from 'src/api/redux/codes';
// Import directly from hook file to avoid circular dependency via hooks/index.ts
import { useGenericComponent } from '../../hooks/useGenericComponent';
import { GenericSelectorModal, GenericNodeComponent, ConnectorType } from '../shared';
import {
  CodeItem,
  codeModalConfig,
  codeNodeConfig,
  transformCode,
} from './config';

interface CodesComponentProps {
  componentId: string;
  initialCodes?: CodeItem[];
  initialConnector?: ConnectorType;
  onUpdate: (componentId: string, data: any) => void;
  onRemove: (componentId: string) => void;
  fromPlaceholder?: boolean;
}

// Extract IDs for Redux storage
const getItemIds = (items: CodeItem[]) => items.map((c) => c.code_name);

const CodesComponent: React.FC<CodesComponentProps> = ({
  componentId,
  initialCodes = [],
  initialConnector = 'ANY',
  onUpdate,
  onRemove,
  fromPlaceholder = false,
}) => {
  // Use generic component hook for state management
  const {
    items: codes,
    connector,
    showModal,
    searchTerm,
    setShowModal,
    setSearchTerm,
    handleSave,
    handleModalClose,
    handleRemoveItem,
    handleConnectorChange,
  } = useGenericComponent<CodeItem>({
    componentId,
    type: 'codes',
    reduxDataField: 'value',
    getItemIds,
    initialItems: initialCodes,
    initialConnector,
    onUpdate,
    onRemove,
    fromPlaceholder,
  });

  // Debounce search term
  const [debouncedSearch] = useDebounce(searchTerm, 300);

  // Fetch search results
  const { data: searchData, isLoading } = useGetCodeSuggestionsQuery({
    q: debouncedSearch,
  });

  // Transform API results to standard format
  const searchResults: CodeItem[] = (searchData && 'results' in searchData ? searchData.results : Array.isArray(searchData) ? searchData : []).map(transformCode);

  return (
    <>
      {codes.length > 0 ? (
        <GenericNodeComponent
          config={codeNodeConfig}
          items={codes}
          connector={connector}
          onRemoveItem={handleRemoveItem}
          onAddMore={() => setShowModal(true)}
          onConnectorChange={handleConnectorChange}
        />
      ) : (
        <button
          type="button"
          className="inline-flex items-center gap-1 px-2 py-1 bg-green-50 border border-green-200 rounded-full cursor-pointer hover:bg-green-100 transition-colors"
          onClick={() => setShowModal(true)}
        >
          <span className="text-green-600 text-xs font-medium">
            Select codes...
          </span>
        </button>
      )}

      <GenericSelectorModal
        config={codeModalConfig}
        isOpen={showModal}
        selectedItems={codes}
        connector={connector}
        isEditing={codes.length > 0}
        onClose={handleModalClose}
        onSave={handleSave}
        searchResults={searchResults}
        isLoading={isLoading}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
      />
    </>
  );
};

export default CodesComponent;

// Static method to render into a DOM element (for contentEditable integration)
export const renderCodesComponent = (
  container: HTMLElement,
  props: CodesComponentProps & {
    initialCodes?: CodeItem[];
    initialConnector?: ConnectorType;
  }
) => {
  const root = createRoot(container);
  root.render(
    <Provider store={store}>
      <CodesComponent {...props} />
    </Provider>
  );
  return root;
};
